import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Phone, Users, Briefcase, Check } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import Reveal from "@/components/shared/Reveal";
import SearchWidget from "@/components/shared/SearchWidget";
import ProviderStrip from "@/components/shared/ProviderStrip";
import PriceBadge from "@/components/shared/PriceBadge";
import CityCard from "@/components/shared/CityCard";
import { findCategory, findCity, BRAND, PROVIDERS } from "@/lib/data";

export default function CategoryDetail() {
  const { slug } = useParams();
  const cat = findCategory(slug);
  if (!cat) return <Navigate to="/categories" replace />;

  const cities = cat.popularCities.map(findCity).filter(Boolean);

  return (
    <PageShell>
      {/* Hero */}
      <section className="bg-[#0B2545] text-white pt-14 md:pt-20 pb-14 md:pb-20" data-testid="category-hero">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="overline-light mb-6">
            <Link to="/categories" className="text-white/70 hover:text-white transition-colors">Categories</Link>
            <span className="mx-2 text-white/40">/</span>
            <span className="text-white">{cat.name}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-7">
              <h1 className="font-serif text-[12vw] md:text-[7vw] lg:text-[5.5vw] leading-[1] tracking-[-0.02em] text-white">
                {cat.name}
              </h1>
              <p className="mt-6 text-white/85 text-lg md:text-xl max-w-2xl leading-relaxed">
                {cat.tagline} {cat.example ? <span className="text-white/60">Popular models: {cat.example}.</span> : null}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <PriceBadge price={cat.startingFrom} size="lg" tone="dark" />
                <div className="inline-flex items-center gap-2 border border-white/25 px-3 py-1.5 font-mono text-xs tracking-widest uppercase text-white/85">
                  <Users className="h-3.5 w-3.5" strokeWidth={1.8} /> {cat.passengers} passengers
                </div>
                <div className="inline-flex items-center gap-2 border border-white/25 px-3 py-1.5 font-mono text-xs tracking-widest uppercase text-white/85">
                  <Briefcase className="h-3.5 w-3.5" strokeWidth={1.8} /> {cat.luggage}
                </div>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <a href={BRAND.phoneHref} className="btn-inverse" data-testid="cat-call">
                  <Phone className="h-4 w-4" strokeWidth={2} /> Call {BRAND.phone}
                </a>
                <Link to={`/contact?car_category=${encodeURIComponent(cat.name)}`} className="btn-ghost-dark" data-testid="cat-quote">
                  Get a free quote <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                </Link>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="relative aspect-[4/3] overflow-hidden border border-white/15 bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center p-6">
                <img
                  src={cat.image}
                  alt={`${cat.name} rental — ${cat.example}`}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="relative -mb-14 md:-mb-16 z-10 mt-12 mx-auto max-w-[1600px] px-5 md:px-10">
          <SearchWidget variant="compact" defaultCategory={cat.name} />
        </div>
      </section>

      <div className="h-14 md:h-16 bg-white" />
      <ProviderStrip />

      {/* About the class */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline mb-4">About the class</div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0B2545] leading-tight">
              What {cat.name.toLowerCase()} rentals are best for.
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-[#5B6472] text-lg leading-relaxed max-w-2xl mb-8">
              {cat.description}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {cat.useCases.map((u) => (
                <div key={u} className="border border-line p-5">
                  <Check className="h-5 w-5 text-[#0B2545] mb-3" strokeWidth={2} />
                  <p className="font-serif text-lg text-[#0B2545]">{u}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Provider brands */}
      <section className="py-16 md:py-20 bg-[#F5F7FA] border-y border-line">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="overline mb-4">Providers compared</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#0B2545] leading-tight mb-8 max-w-3xl">
            {cat.name} availability compared across every major US rental provider.
          </h2>
          <ul className="flex flex-wrap gap-3">
            {PROVIDERS.map((p) => (
              <li key={p} className="border border-line bg-white px-4 py-2 text-sm font-medium text-[#0B2545]">
                {p}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Popular cities */}
      {cities.length > 0 && (
        <section className="py-20 md:py-28 bg-white">
          <div className="mx-auto max-w-[1600px] px-5 md:px-10">
            <div className="overline mb-4">Where it's popular</div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0B2545] leading-tight mb-12">
              Top US cities for {cat.name.toLowerCase()} rentals.
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
              {cities.map((c, i) => (
                <CityCard key={c.slug} city={c} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0B2545] text-white">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <h2 className="font-serif text-4xl md:text-5xl leading-tight text-white max-w-3xl">
            Ready for a {cat.name.toLowerCase()} rental quote? Call or submit an inquiry.
          </h2>
          <div className="mt-10 flex flex-col md:flex-row gap-3">
            <a href={BRAND.phoneHref} className="btn-inverse">
              <Phone className="h-4 w-4" strokeWidth={2} /> {BRAND.phone}
            </a>
            <Link to={`/contact?car_category=${encodeURIComponent(cat.name)}`} className="btn-ghost-dark">
              Get a free quote <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          </div>
          <p className="mt-8 text-xs text-white/55 max-w-2xl leading-relaxed">
            Pricing shown is estimated starting-from pricing based on recent averages across
            providers. Live availability and final pricing are confirmed by our team.
          </p>
        </div>
      </section>
    </PageShell>
  );
}
